import React, { useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LangSwitcher from "../LangSwitcher";
import { popToPrevSafeRoute } from "../../hooks/useLastSafeRoute";
import { getPdxIfuHref } from "../../utils/navigation/ifuLink";

function NavBar({ className = "" }) {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();

  // Bottom-nav pages (logo shown on these; otherwise show back button)
  const lang = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en-GB"
  ).toLowerCase();
  const ifuHref = getPdxIfuHref(lang);

  const path = location?.pathname || "";

  // Logo should show on bottom-nav pages EXCEPT home
  const showLogo = path === "/notice" || path === "/about";

  // Home should show neither logo nor back button
  const hideLeftControl = path === "/home";

  // back is only available if we actually have a safe previous route
  const hasSafeBack = useMemo(() => {
    return !hideLeftControl && !showLogo;
  }, [hideLeftControl, showLogo]);

  const showBackButton = !showLogo && !hideLeftControl && hasSafeBack;

  // inside NavBar.js
  const handleBack = useCallback(() => {
    const current = `${location?.pathname || ""}${location?.search || ""}`;

    const dest = popToPrevSafeRoute("/home", current);

    navigate(dest); // do NOT replace while debugging
  }, [navigate, location?.pathname, location?.search]);

  return (
    <>
      <header
        className={`grid grid-cols-3 items-center justify-center w-full gap-4 z-50 mb-2 ${className}`}
      >
        {/* Left: logo OR back button */}
        <div className="flex items-center gap-2 min-w-0 w-fit justify-start py-2 pl-2 md:pl-4 ">
          {showBackButton ? (
            <button
              type="button"
              onClick={handleBack}
              className="group flex !flex-row gap-1"
              aria-label={t("navBar.back", "Back")}
            >
              <span
                className="material-symbols-outlined text-typography text-sm group-hover:text-primary group-focus-visible:text-primary"
                aria-hidden="true"
              >
                arrow_back_ios
              </span>
            </button>
          ) : (
            <div className="h-12 w-[120px]" aria-hidden="true" />
          )}
        </div>
        <div className="flex items-center justify-center w-full ">
          <img
            src="/pdx_logo.png"
            alt="pDx"
            className="h-12 w-auto max-w-none title-text self-center "
            loading="lazy"
          />
        </div>
        {/* Mobile right: Guide CTA + hamburger */}
        <div className="flex w-full items-center justify-end p-1 pr-2 ">
          <LangSwitcher />
        </div>
      </header>
    </>
  );
}

export default React.memo(NavBar);
